import { useSyncExternalStore } from "react";
import ensureError from "@-/com.npmjs.ensure-error";
import onetime from "@-/com.npmjs.onetime";
import type { NeuralSentenceVoice } from "@-/dev.improve.haniverse.lib.sentence.speech.voice-catalog";
import {
  downloadNeuralSentenceVoice,
  onSentenceVoiceFilesChanged,
  removeNeuralSentenceVoices,
} from "#sentence-voice-files";
import { clearSentenceAudioCache } from "#synthesize-sentence-audio";

export type SentenceVoiceDownloadState =
  | { status: "idle" }
  | { fraction: number; status: "downloading" }
  | { message: string; status: "failed" };

const IDLE: SentenceVoiceDownloadState = { status: "idle" };

/**
 * Download progress per voice, shared by every surface that offers voices (the first-play prompt
 * and Settings), so a download started in one shows its progress in the other, and starting the
 * same voice twice joins the download already running.
 */
const states = new Map<string, SentenceVoiceDownloadState>();
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
/** Bumped whenever files are added or removed, so synchronous "installed?" reads re-run. */
const filesRevision = { value: 0 };

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

// Files can also arrive without a download here: the App Store's asset pack.
const followFileChanges = onetime(() =>
  onSentenceVoiceFilesChanged(() => {
    filesRevision.value += 1;
    emit();
  }),
);

function setState(voiceId: string, state: SentenceVoiceDownloadState): void {
  states.set(voiceId, state);
  emit();
}

function subscribe(listener: () => void): () => void {
  followFileChanges();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function startNeuralSentenceVoiceDownload(voice: NeuralSentenceVoice): Promise<void> {
  const running = inFlight.get(voice.id);
  if (running !== undefined) {
    return running;
  }
  setState(voice.id, { fraction: 0, status: "downloading" });
  let lastPercent = 0;
  const download = downloadNeuralSentenceVoice(voice, {
    onProgress: (fraction) => {
      // Whole percents only: progress events arrive far faster than anyone can read them.
      const percent = Math.floor(fraction * 100);
      if (percent !== lastPercent) {
        lastPercent = percent;
        setState(voice.id, { fraction, status: "downloading" });
      }
    },
  })
    .then(
      () => {
        filesRevision.value += 1;
        setState(voice.id, IDLE);
      },
      (error: unknown) => {
        setState(voice.id, { message: ensureError(error).message, status: "failed" });
        throw error;
      },
    )
    .finally(() => {
      inFlight.delete(voice.id);
    });
  inFlight.set(voice.id, download);
  return download;
}

export async function removeAllNeuralSentenceVoices(): Promise<void> {
  await removeNeuralSentenceVoices();
  clearSentenceAudioCache();
  filesRevision.value += 1;
  emit();
}

export function useNeuralSentenceVoiceDownload(voiceId: string): SentenceVoiceDownloadState {
  return useSyncExternalStore(
    subscribe,
    () => states.get(voiceId) ?? IDLE,
    () => IDLE,
  );
}

/** Changes whenever voice files are installed or removed; use it as a dependency for sync reads. */
export function useSentenceVoiceFilesRevision(): number {
  return useSyncExternalStore(
    subscribe,
    () => filesRevision.value,
    () => 0,
  );
}
